import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { translate } from '@/services/TranslationService';
import WEBAPI from '@/services/WEBAPI';
import NOVOLoader from '../../shared/components/NOVOLoader';
import { changeAndPostProfile } from '../profile/actions';
import { getFallbackRedirect, getRedirectForAction } from './actionHandlers';
import { setDeeplinkData } from './actions';
import { FALLBACK_REDIRECT_TIMEOUT_MS, ROUTE_ACTIONS } from './constants';

const findApplicationTypeByCode = (applicationTypes, code) => {
  if (!code) return null;

  return (applicationTypes || []).find((applicationType) => {
    const possibleCodes = [
      applicationType?.code,
      applicationType?.name,
      applicationType?.id,
      applicationType?.slug,
    ].filter(Boolean);

    return possibleCodes.some(
      (possibleCode) => String(possibleCode) === String(code)
    );
  });
};

const RouteHandler = ({ location, setHeaderTitle }) => {
  const dispatch = useDispatch();

  const isAuthenticated = useSelector(
    (state) => !!state.authReducer.auth_token
  );
  const [isProfileSyncing, setIsProfileSyncing] = useState(false);
  const [isFetchingApplicationTypes, setIsFetchingApplicationTypes] =
    useState(false);
  const [hasFetchedApplicationTypes, setHasFetchedApplicationTypes] =
    useState(false);
  const [fallbackRedirect, setFallbackRedirect] = useState(false);
  const [hasProcessedAuthParams, setHasProcessedAuthParams] = useState(false);
  const [persistedDeeplinkKey, setPersistedDeeplinkKey] = useState('');

  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return {
      action: params.get('action') || '',
      code: params.get('code') || '',
      authority: params.get('authority') || '',
      country: params.get('country') || '',
      lang:
        params.get('lang') ||
        params.get('language') ||
        params.get('country') ||
        '',
      applicationsLicensePlate: params.get('applications_license_plate') || '',
      applicationsCode: params.get('applications_code') || '',
    };
  }, [location.search]);

  const hasDeeplinkParams = useMemo(
    () =>
      !!queryParams.action ||
      !!queryParams.code ||
      !!queryParams.authority ||
      !!queryParams.country ||
      !!queryParams.lang ||
      !!queryParams.applicationsLicensePlate ||
      !!queryParams.applicationsCode,
    [
      queryParams.action,
      queryParams.applicationsCode,
      queryParams.applicationsLicensePlate,
      queryParams.authority,
      queryParams.code,
      queryParams.country,
      queryParams.lang,
    ]
  );

  const deeplinkKey = useMemo(
    () =>
      [
        queryParams.action || '',
        queryParams.code || '',
        queryParams.authority || '',
        queryParams.country || '',
        queryParams.lang || '',
        queryParams.applicationsLicensePlate || '',
        queryParams.applicationsCode || '',
      ].join('|'),
    [
      queryParams.action,
      queryParams.applicationsCode,
      queryParams.applicationsLicensePlate,
      queryParams.authority,
      queryParams.code,
      queryParams.country,
      queryParams.lang,
    ]
  );

  useEffect(() => {
    if (!hasDeeplinkParams) {
      setPersistedDeeplinkKey('');
      return;
    }

    if (persistedDeeplinkKey === deeplinkKey) return;

    dispatch(
      setDeeplinkData({
        action: queryParams.action || null,
        payload_code: queryParams.code || null,
        authority: queryParams.authority || null,
        country: queryParams.country || null,
        meta: {
          application_licence_plate:
            queryParams.applicationsLicensePlate || null,
          applications_code: queryParams.applicationsCode || null,
        },
        needsDeeplinkRedirection: !!queryParams.action,
      })
    );
    setPersistedDeeplinkKey(deeplinkKey);
  }, [
    dispatch,
    deeplinkKey,
    hasDeeplinkParams,
    persistedDeeplinkKey,
    queryParams.action,
    queryParams.applicationsCode,
    queryParams.applicationsLicensePlate,
    queryParams.authority,
    queryParams.code,
    queryParams.country,
    queryParams.lang,
  ]);

  useEffect(() => {
    const title = translate('Route handler') || 'Route handler';
    setHeaderTitle?.(title);

    const isApplicationsAction =
      queryParams.action?.toLowerCase() === ROUTE_ACTIONS.APPLICATIONS;
    const hasAuthParams =
      !isApplicationsAction &&
      (!!queryParams.authority || !!queryParams.country);

    if (!hasAuthParams) {
      setHasProcessedAuthParams(true);
      setIsProfileSyncing(false);
      return;
    }

    let isCancelled = false;

    const updateProfile = async () => {
      setHasProcessedAuthParams(false);
      setIsProfileSyncing(true);
      try {
        await dispatch(
          changeAndPostProfile({
            authority: queryParams.authority,
            country: queryParams.country,
          })
        );
      } catch (error) {
        console.error('Failed to update profile from route handler', error);
      } finally {
        if (!isCancelled) {
          setIsProfileSyncing(false);
          setHasProcessedAuthParams(true);
        }
      }
    };

    updateProfile();

    return () => {
      isCancelled = true;
    };
  }, [
    dispatch,
    queryParams.action,
    queryParams.authority,
    queryParams.country,
    setHeaderTitle,
  ]);

  useEffect(() => {
    const isApplicationsAction =
      queryParams.action?.toLowerCase() === ROUTE_ACTIONS.APPLICATIONS;

    if (!isApplicationsAction) {
      setIsFetchingApplicationTypes(false);
      setHasFetchedApplicationTypes(true);
      return;
    }

    if (!queryParams.authority) {
      setIsFetchingApplicationTypes(false);
      setHasFetchedApplicationTypes(true);
      return;
    }

    let isCancelled = false;

    const fetchPublicApplicationTypes = async () => {
      setIsFetchingApplicationTypes(true);
      setHasFetchedApplicationTypes(false);

      try {
        const response = await WEBAPI.getPublicApplicationTypes(
          queryParams.authority,
          queryParams.lang || 'en'
        );

        console.log('[RouteHandler][applications] public types response', {
          authority: queryParams.authority,
          lang: queryParams.lang || 'en',
          code: queryParams.code,
          response,
        });

        if (isCancelled) return;

        const publicApplicationTypes = response.result || [];
        const publicApplicationType = findApplicationTypeByCode(
          publicApplicationTypes,
          queryParams.code
        );

        console.log('[RouteHandler][applications] form lookup result', {
          found: !!publicApplicationType,
          requestedCode: queryParams.code,
          matchedApplicationType: publicApplicationType || null,
        });

        dispatch(
          setDeeplinkData({
            action: queryParams.action || null,
            payload_code: queryParams.code || null,
            authority: queryParams.authority || null,
            country: queryParams.country || null,
            meta: {
              application_licence_plate:
                queryParams.applicationsLicensePlate || null,
              applications_code: queryParams.applicationsCode || null,
              public_application_types: publicApplicationTypes,
              public_application_types_error: null,
              public_application_type: publicApplicationType || null,
              public_application_type_found: !!publicApplicationType,
            },
            needsDeeplinkRedirection: true,
          })
        );
      } catch (error) {
        if (isCancelled) return;

        console.log('[RouteHandler][applications] public types request failed', {
          authority: queryParams.authority,
          lang: queryParams.lang || 'en',
          code: queryParams.code,
          error,
        });

        dispatch(
          setDeeplinkData({
            action: queryParams.action || null,
            payload_code: queryParams.code || null,
            authority: queryParams.authority || null,
            country: queryParams.country || null,
            meta: {
              application_licence_plate:
                queryParams.applicationsLicensePlate || null,
              applications_code: queryParams.applicationsCode || null,
              public_application_types: [],
              public_application_types_error: true,
              public_application_type: null,
              public_application_type_found: false,
            },
            needsDeeplinkRedirection: true,
          })
        );
      } finally {
        if (!isCancelled) {
          setIsFetchingApplicationTypes(false);
          setHasFetchedApplicationTypes(true);
        }
      }
    };

    fetchPublicApplicationTypes();

    return () => {
      isCancelled = true;
    };
  }, [
    dispatch,
    queryParams.action,
    queryParams.applicationsCode,
    queryParams.applicationsLicensePlate,
    queryParams.authority,
    queryParams.code,
    queryParams.country,
    queryParams.lang,
  ]);

  const redirectComponent = useMemo(
    () => getRedirectForAction({ ...queryParams, isAuthenticated }),
    [queryParams, isAuthenticated]
  );

  useEffect(() => {
    if (!queryParams.action) return;
    if (!redirectComponent && !fallbackRedirect) return;
  }, [dispatch, queryParams.action, redirectComponent, fallbackRedirect]);

  const shouldWaitForProfileSync =
    queryParams.action?.toLowerCase() !== ROUTE_ACTIONS.APPLICATIONS &&
    (!!queryParams.authority || !!queryParams.country) &&
    (!hasProcessedAuthParams || isProfileSyncing);
  const shouldWaitForDeeplinkPersistence =
    hasDeeplinkParams && persistedDeeplinkKey !== deeplinkKey;
  const shouldWaitForApplicationTypes =
    queryParams.action?.toLowerCase() === ROUTE_ACTIONS.APPLICATIONS &&
    queryParams.authority &&
    (!hasFetchedApplicationTypes || isFetchingApplicationTypes);

  useEffect(() => {
    setFallbackRedirect(false);

    if (redirectComponent) return;

    const timer = setTimeout(() => {
      setFallbackRedirect(true);
    }, FALLBACK_REDIRECT_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [queryParams.action, redirectComponent]);

  if (
    redirectComponent &&
    !shouldWaitForProfileSync &&
    !shouldWaitForDeeplinkPersistence &&
    !shouldWaitForApplicationTypes
  ) {
    return redirectComponent;
  }

  if (fallbackRedirect) {
    return getFallbackRedirect();
  }

  return (
    <div className="flex flex-1 items-center justify-center">
      <NOVOLoader />
    </div>
  );
};

export default withRouter(RouteHandler);
